"use client";

import { useState } from "react";
import { useLang } from "@/contexts/language-context";
import AnimatedSection, { AnimatedElement } from "../AnimatedSection";
import FeatureDetailModal from "../FeatureDetailModal";
import { getFeaturesByVertical, type MarketingFeature } from "@/lib/marketing/features-data";

export default function NightlifePerks() {
  const { t } = useLang();
  const [openFeature, setOpenFeature] = useState<MarketingFeature | null>(null);
  const perks = getFeaturesByVertical("nightlife");

  return (
    <section className="py-24 px-6 bg-[#0A0814] text-white">
      <div className="max-w-[1200px] mx-auto">
        <AnimatedSection className="text-center mb-14">
          <div className="text-[11px] font-bold tracking-widest uppercase text-[#C4B5FD] mb-4">
            {t("The whole venue", "Todo el venue")}
          </div>
          <h2 className="text-4xl md:text-[52px] font-black tracking-tight text-white leading-[1.05]">
            {t("Six tools.", "Seis herramientas.")}
            <br />
            <span className="text-white/30">
              {t("One Friday night.", "Una noche de viernes.")}
            </span>
          </h2>
          <p className="text-[15px] font-medium text-white/55 max-w-2xl mx-auto mt-6">
            {t(
              "Everything you'd buy from five different vendors — built into one platform that talks to itself.",
              "Todo lo que comprarías a cinco vendors distintos — construido en una sola plataforma que se habla a sí misma."
            )}
          </p>
          <p className="mt-6 text-[12px] font-bold tracking-wide uppercase text-[#F472B6]/80">
            {t("Tap any tool to see how it works →", "Toca cualquier herramienta para ver cómo funciona →")}
          </p>
        </AnimatedSection>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {perks.map((perk, i) => {
            const Icon = perk.icon;
            const accent = perk.accent ?? "#C4B5FD";
            return (
              <AnimatedElement
                key={perk.slug}
                delay={0.08 * i}
                className="bg-white/[0.04] border border-white/10 rounded-[32px] hover:bg-white/[0.06] hover:border-white/15 hover:-translate-y-1 transition-all group focus-within:ring-2 focus-within:ring-[#7C3AED]/40"
              >
                <button
                  type="button"
                  onClick={() => setOpenFeature(perk)}
                  aria-label={t(
                    `Learn more about ${perk.card.title.en}`,
                    `Aprende más sobre ${perk.card.title.es}`
                  )}
                  className="block w-full h-full text-left p-8 outline-none cursor-pointer"
                >
                  <div
                    className="w-12 h-12 rounded-[16px] flex items-center justify-center shrink-0 mb-5 group-hover:scale-110 transition-transform"
                    style={{ backgroundColor: `${accent}2C` }}
                  >
                    <Icon className="w-5 h-5" style={{ color: accent }} />
                  </div>
                  <h3 className="text-[18px] font-black text-white mb-2">
                    {t(perk.card.title.en, perk.card.title.es)}
                  </h3>
                  <p className="text-[14px] font-medium text-white/55 leading-relaxed mb-4">
                    {t(perk.card.desc.en, perk.card.desc.es)}
                  </p>
                  <span
                    className="inline-flex items-center gap-1 text-[12px] font-bold opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{ color: accent }}
                  >
                    {t("Learn more →", "Más detalles →")}
                  </span>
                </button>
              </AnimatedElement>
            );
          })}
        </div>
      </div>

      <FeatureDetailModal feature={openFeature} onClose={() => setOpenFeature(null)} />
    </section>
  );
}
